import * as i0 from '@angular/core';
import { signal, computed, Optional, Injectable, ChangeDetectionStrategy, Component, makeEnvironmentProviders } from '@angular/core';
import { filter, take, map, catchError, EMPTY, shareReplay, of, mergeMap } from 'rxjs';
import * as i1 from 'angular-auth-oidc-client';
import { EventTypes, provideAuth, LogLevel } from 'angular-auth-oidc-client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as i2 from '@angular/router';
import * as i2$1 from '@angular/platform-browser';
class AuthentificateConfig {
  constructor() {
    /** Logo path to display during the login. */
    this.logoPath = "";
    /** Route to redirect to, after a successfull login. */
    this.afterLoginRoute = "";
    /** Url of the authority server */
    this.authorityUrl = "";
    /** OAuth2 client id of this client. */
    this.clientId = "";
  }
}
let AuthService = /*#__PURE__*/(() => {
  class AuthService {
    constructor(oidc, _router, _eventService, _config) {
      this.oidc = oidc;
      this._router = _router;
      this._eventService = _eventService;
      this._config = _config;
      this._checkAuthResult$ = null;
      this._jwtTokenSignal = signal("");
      this._isLoggedInSignal = signal(false);
      this.isLoggedIn = computed(() => this._isLoggedInSignal());
      /** Current session jwt token. */
      this.jwtToken = computed(() => this._jwtTokenSignal());
      this._eventService.registerForEvents().pipe(filter(notification => notification.type === EventTypes.UserDataChanged || notification.type === EventTypes.NewAuthenticationResult), takeUntilDestroyed()).subscribe(() => this.oidc.getIdToken().pipe(take(1)).subscribe(idToken => this._jwtTokenSignal.set(idToken)));
    }
    /**
     * Silent login using iFrame.
     * @returns Returns an observable with the a LoginResponse.
     */
    checkAuthIncludingServer() {
      return this.oidc.checkAuthIncludingServer().pipe(map(value => {
        if (value) this._isLoggedInSignal.set(value.isAuthenticated);else this._isLoggedInSignal.set(false);
        return value;
      }));
    }
    /**
     * Triggers the login routine.
     * @return Returns the authentification state.
     */
    triggerOrCheckLogin() {
      if (!this._checkAuthResult$) {
        this._checkAuthResult$ = this.oidc.checkAuth().pipe(catchError(err => {
          console.error(err);
          // Kills the cache.
          window.location.href = (document.querySelector('base') || {}).href + "?h=" + new Date().getTime().toString(16);
          return EMPTY;
        }), shareReplay(1));
      }
      return this._checkAuthResult$.pipe(map(value => {
        this._isLoggedInSignal.set(value.isAuthenticated);
        return value.isAuthenticated;
      }));
    }
    /**
     * @returns Returns a signal with the user data result.
     */
    get userInfo() {
      return this.oidc.userData;
    }
    /** Starts the login flow. */
    login() {
      this.oidc.authorize();
    }
    /** User logout */
    doLogout() {
      this.oidc.getIdToken().pipe(take(1)).subscribe(id_token => {
        this.oidc.logoff(undefined, {
          customParams: {
            "id_token_hint": id_token
          }
        }).pipe(take(1)).subscribe();
      });
    }
    /** If called after a successful login the user is redirected to the last destination. */
    handleRedirection() {
      let url = sessionStorage.getItem("lastUrl");
      if (url)
        // Checks if there is a url.
        {
          // Back to the initial page.
          sessionStorage.removeItem("lastUrl");
          this._router.navigateByUrl(url);
        } else this._router.navigate([this._config?.afterLoginRoute]); // Redirects to the route specified by the config.
    }
    static {
      this.ɵfac = function AuthService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AuthService)(i0.ɵɵinject(i1.OidcSecurityService), i0.ɵɵinject(i2.Router), i0.ɵɵinject(i1.PublicEventsService), i0.ɵɵinject(AuthentificateConfig, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthService,
        factory: AuthService.ɵfac
      });
    }
  }
  return AuthService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AuthGuard = /*#__PURE__*/(() => {
  class AuthGuard {
    constructor(_authService, _router) {
      this._authService = _authService;
      this._router = _router;
    }
    getUrl(route) {
      let url = "/";
      // Der ganze Routerbaum wird von oben runter gegangen.
      // Dabei werden die einzelnen URL Segmente zusammengebaut.
      let routes = route.pathFromRoot;
      for (const r of routes) {
        for (const seg of r.url) {
          if (url[url.length - 1] !== '/') url += "/";
          url += seg.path;
        }
      }
      // Für Abas Ids müssen "()" uri encoded werden, da angular dies sonst als Subroute interpretiert.
      url = url.replace(/\(/g, "%28").replace(/\)/g, "%29");
      // Hängt die Parameter an die URL an.
      let keys = Object.keys(route.queryParams);
      if (keys.length > 0) {
        url += "?";
        for (const key of keys) {
          if (url[url.length - 1] !== '&' && url[url.length - 1] !== '?') url += "&";
          url += key + "=" + route.queryParams[key];
        }
      }
      return url;
    }
    getUrlFromSegments(segments) {
      let url = "/";
      // Alle Url Segmente werden concatenated.
      for (const seg of segments) {
        if (url[url.length - 1] !== '/') url += "/";
        url += seg.path;
      }
      // Für Abas Ids müssen "()" uri encoded werden, da angular dies sonst als Subroute interpretiert.
      url = url.replace(/\(/g, "%28").replace(/\)/g, "%29");
      return url;
    }
    isLoggedIn() {
      return this._authService.triggerOrCheckLogin().pipe(map(isLoggedIn => {
        if (isLoggedIn) return isLoggedIn;
        // If the user isn't logged in, redirect to the login status page.
        return this._router.parseUrl('/auth/login-status');
      }));
    }
    canLoad(route, segments) {
      const url = this.getUrlFromSegments(segments);
      return this.isLoggedIn().pipe(map(loginStatus => {
        if (loginStatus !== true) {
          if (url !== "") sessionStorage.setItem("lastUrl", url);
          return loginStatus;
        }
        return true;
      }));
    }
    canActivate(route, state) {
      const url = this.getUrl(route);
      return this.isLoggedIn().pipe(map(loginStatus => {
        if (loginStatus !== true) {
          if (url !== "") sessionStorage.setItem("lastUrl", url);
          return loginStatus;
        }
        return true;
      }));
    }
    static {
      this.ɵfac = function AuthGuard_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AuthGuard)(i0.ɵɵinject(AuthService), i0.ɵɵinject(i2.Router));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthGuard,
        factory: AuthGuard.ɵfac
      });
    }
  }
  return AuthGuard;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var Roles = /*#__PURE__*/function (Roles) {
  Roles["DEVICE_GROUP_ADMIN"] = "deviceGroupAdmin";
  Roles["LOGIN_CONNECT"] = "loginConnect";
  Roles["MDE_EDITOR"] = "mdeEditor";
  Roles["ORDER_TOKEN"] = "orderToken";
  Roles["ORDER_USER"] = "orderUser";
  Roles["PRODUCT_PAGE_EDITOR"] = "productPageEditor";
  Roles["SERVICE_ADMIN"] = "serviceAdmin";
  Roles["SERVICE_USER"] = "serviceUser";
  Roles["TRANSLATOR"] = "translator";
  Roles["TRANSLATOR_MASTER"] = "translatorMaster";
  Roles["USER_MANAGEMENT"] = "userManagement";
  Roles["SALESOFFER_TOKEN"] = "salesOfferToken";
  Roles["CRM_ACCESS"] = "crmAccess";
  Roles["CRM_MANAGER"] = "crmManager";
  Roles["CRM_SERVICE_POTENTIAL_USER"] = "crmServicePotentialUser";
  Roles["DAM_READER"] = "damReader";
  Roles["DAM_WRITER"] = "damWriter";
  Roles["SEMINAR_MANAGER"] = "seminarManager";
  Roles["SEMINAR_ADMIN"] = "seminarAdmin";
  Roles["TESTGAS_MANAGER"] = "testGasManager";
  Roles["WORKSPACE_ADMIN"] = "workspaceAdmin";
  Roles["WORKSPACE_BOOKER"] = "workspaceBooker";
  return Roles;
}(Roles || {});
let RoleService = /*#__PURE__*/(() => {
  class RoleService {
    constructor(_oidcSecurityService) {
      this._oidcSecurityService = _oidcSecurityService;
    }
    /** @returns Returns the user roles */
    getRoles() {
      return this._oidcSecurityService.getUserData().pipe(filter(userdata => userdata), map(userdata => userdata.role));
    }
    /**
     * @param role The role the must have
     * @returns Returns true if the user has the role.
     */
    hasRole(role) {
      return this.getRoles().pipe(map(roles => {
        if (!roles) return false;
        let ret = false;
        if (roles.hasOwnProperty(role)) ret = roles[role];
        return ret;
      }));
    }
    /**
     * @param roles Roles which the user should have
     * @param or If true, only one role must exists, otherwise all.
     * @returns Returns true, if the condition passed.
     */
    hasRoles(roles, or = false) {
      let ret = !or;
      for (const role of roles) {
        let hasRole = !or;
        this.hasRole(role).subscribe(x => hasRole = x);
        if (or) ret = ret || hasRole;else ret = ret && hasRole;
      }
      return of(ret);
    }
    /**
     * @return Returns all target languages.
     */
    get targetLanguages() {
      return this.getRoles().pipe(map(roles => JSON.parse(roles["translationLanguages"])));
    }
    static {
      this.ɵfac = function RoleService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RoleService)(i0.ɵɵinject(i1.OidcSecurityService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RoleService,
        factory: RoleService.ɵfac
      });
    }
  }
  return RoleService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RoleGuard = /*#__PURE__*/(() => {
  class RoleGuard extends AuthGuard {
    constructor(_roleService, authService, router) {
      super(authService, router);
      this._roleService = _roleService;
      this.authService = authService;
      this.router = router;
    }
    checkRoles(roles, or, url) {
      return this.isLoggedIn().pipe(mergeMap(loginStatus => {
        if (loginStatus !== true) {
          if (url !== "") sessionStorage.setItem("lastUrl", url);
          return of(loginStatus);
        }
        let ret = of(true);
        // Überprüft die Rollen eines Users
        if (roles) ret = this._roleService.hasRoles(roles, or);
        return ret;
      }), map(ret => {
        if (ret !== true) return ret;
        // Sollte hasRoles false sein, dann landen wir hier.
        if (!ret) ret = this.router.parseUrl('/auth/login-status');
        return ret;
      }));
    }
    canLoad(route, segments) {
      let or = route.data?.or ? route.data.or : false;
      return this.checkRoles(route.data?.roles, or, this.getUrlFromSegments(segments));
    }
    canActivate(route, state) {
      let or = route.data?.or ? route.data.or : false;
      return this.checkRoles(route.data.roles, or, this.getUrl(route));
    }
    static {
      this.ɵfac = function RoleGuard_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RoleGuard)(i0.ɵɵinject(RoleService), i0.ɵɵinject(AuthService), i0.ɵɵinject(i2.Router));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RoleGuard,
        factory: RoleGuard.ɵfac
      });
    }
  }
  return RoleGuard;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CallbackComponent = /*#__PURE__*/(() => {
  class CallbackComponent {
    constructor(_authService) {
      this._authService = _authService;
      this._authService.triggerOrCheckLogin().pipe(takeUntilDestroyed()).subscribe(() => {
        this._authService.handleRedirection();
      });
    }
    static {
      this.ɵfac = function CallbackComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CallbackComponent)(i0.ɵɵdirectiveInject(AuthService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: CallbackComponent,
        selectors: [["ng-component"]],
        decls: 0,
        vars: 0,
        template: function CallbackComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return CallbackComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LoginStatusComponent = /*#__PURE__*/(() => {
  class LoginStatusComponent {
    constructor(_authService, sanitizer, _destroyRef, config) {
      this._authService = _authService;
      this.sanitizer = sanitizer;
      this._destroyRef = _destroyRef;
      this.config = config;
    }
    ngOnInit() {
      this._authService.triggerOrCheckLogin().pipe(takeUntilDestroyed(this._destroyRef)).subscribe(isLoggedIn => {
        if (!isLoggedIn) this._authService.login();else this._authService.handleRedirection();
      });
    }
    static {
      this.ɵfac = function LoginStatusComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LoginStatusComponent)(i0.ɵɵdirectiveInject(AuthService), i0.ɵɵdirectiveInject(i2$1.DomSanitizer), i0.ɵɵdirectiveInject(i0.DestroyRef), i0.ɵɵdirectiveInject(AuthentificateConfig, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: LoginStatusComponent,
        selectors: [["app-login-status"]],
        decls: 3,
        vars: 1,
        consts: [[1, "row"], [1, "s12", "m-offset-3", "m6", "col", "content"], ["width", "256px", "alt", "loading", 3, "src"]],
        template: function LoginStatusComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
            i0.ɵɵelement(2, "img", 2);
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("src", ctx.sanitizer.bypassSecurityTrustUrl(ctx.config == null ? null : ctx.config.logoPath), i0.ɵɵsanitizeUrl);
          }
        },
        styles: ["[_nghost-%COMP%]{display:flex;align-items:center;justify-content:center;min-height:calc(100vh - 210px)}@media only screen and (max-width : 992px){[_nghost-%COMP%]{min-height:calc(100vh - 140px)}}.content[_ngcontent-%COMP%]{display:flex;flex-direction:column;align-items:center}h2[_ngcontent-%COMP%]{margin-bottom:20px}mat-card[_ngcontent-%COMP%]{padding:5rem}mat-spinner[_ngcontent-%COMP%]{margin-top:25px}img[_ngcontent-%COMP%]{border-radius:50px;box-shadow:0 0 #000;transform:scale(1);animation:_ngcontent-%COMP%_pulse 2s infinite}@keyframes _ngcontent-%COMP%_pulse{0%{transform:scale(.95);box-shadow:0 0 #e2001ab3}70%{transform:scale(1);box-shadow:0 0 0 10px #0000}to{transform:scale(.95);box-shadow:0 0 #0000}}"],
        changeDetection: 0
      });
    }
  }
  return LoginStatusComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const authentificationRoutes = [{
  path: 'login-status',
  component: LoginStatusComponent
}, {
  path: 'callback',
  component: CallbackComponent
}];
function provideAuthentification(config) {
  const providers = [AuthService, RoleService, AuthGuard, RoleGuard, {
    provide: AuthentificateConfig,
    useValue: config
  }, provideAuth({
    config: {
      triggerAuthorizationResultEvent: true,
      authority: config.authorityUrl,
      redirectUrl: (document.querySelector('base') || {}).href + "auth/callback",
      postLogoutRedirectUri: (document.querySelector('base') || {}).href,
      clientId: config.clientId,
      scope: 'openid offline_access esders',
      // 'openid profile ' + your scopes
      responseType: 'code',
      silentRenew: true,
      useRefreshToken: false,
      silentRenewUrl: (document.querySelector('base') || {}).href + 'silent-renew.html',
      ignoreNonceAfterRefresh: true,
      renewTimeBeforeTokenExpiresInSeconds: 10,
      maxIdTokenIatOffsetAllowedInSeconds: 300,
      logLevel: config.logLevel ?? LogLevel.None
    }
  })];
  return makeEnvironmentProviders(providers);
}

/*
 * Public API Surface of authentificate
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthGuard, AuthService, AuthentificateConfig, RoleGuard, RoleService, Roles, authentificationRoutes, provideAuthentification };
